/**
 * Zugang zur API.
 *
 * Das Zugriffstoken liegt nur im Arbeitsspeicher, nicht im localStorage. Damit
 * kann ein eingeschleustes Skript es nicht auslesen. Das Erneuerungstoken
 * liegt in einem httpOnly-Cookie und ist fuer JavaScript unsichtbar.
 */

let accessToken: string | null = null;

export interface SessionUser {
  id: string;
  email: string;
  role: "admin" | "lead" | "employee" | "customer";
  displayName: string;
  locale: string;
  customerId: string | null;
}

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    message: string,
    /**
     * Zusatzangaben der API, etwa die Liste der noch offenen Pruefpunkte.
     * Fehlte dieses Feld, kam die Begruendung des Servers in der Oberflaeche
     * nie an und der Nutzer sah nur "Der Bericht ist nicht vollstaendig",
     * ohne zu erfahren, welche Punkte gemeint sind.
     */
    public readonly details?: unknown
  ) {
    super(message);
  }
}

async function request<T>(path: string, init: RequestInit = {}, retry = true): Promise<T> {
  // content-type nur setzen, wenn wirklich ein Rumpf mitgeht.
  //
  // Ohne diese Unterscheidung schickt der Browser bei DELETE und bei einem
  // POST ohne Daten den Kopf "application/json" mit leerem Rumpf. Fastify
  // versucht dann, nichts als JSON zu lesen, und bricht ab. Sichtbar war das
  // als "Unerwarteter Fehler" beim Wiederanwaehlen eines Bausteins und beim
  // Archivieren. Der Fehler lag nicht dort, sondern hier.
  //
  // Bei einem Formularrumpf wird der Kopf ebenfalls nicht gesetzt: den setzt
  // der Browser fuer multipart selbst, samt Trennzeichen. Wer ihn hier auf
  // JSON stellt, bekommt vom Server "Der Anfragekoerper ist kein gueltiges
  // JSON", und zwar an einer Stelle, an der niemand nach der Ursache sucht.
  const jsonRumpf = init.body !== undefined && !(init.body instanceof FormData);
  const res = await fetch(`/api/v1${path}`, {
    ...init,
    credentials: "include",
    headers: {
      ...(jsonRumpf ? { "content-type": "application/json" } : {}),
      ...(accessToken ? { authorization: `Bearer ${accessToken}` } : {}),
      ...init.headers
    }
  });

  // Abgelaufenes Zugriffstoken: einmal still erneuern, dann wiederholen
  if (res.status === 401 && retry && accessToken) {
    const renewed = await refresh();
    if (renewed) return request<T>(path, init, false);
  }

  const body = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new ApiError(res.status, body.error ?? "error", body.message ?? "Unbekannter Fehler", body.details);
  }
  return body as T;
}

export async function login(email: string, password: string) {
  const body = await request<{ accessToken: string; user: SessionUser }>("/auth/login", {
    method: "POST",
    body: JSON.stringify({ email, password, deviceLabel: navigator.userAgent.slice(0, 100) })
  });
  accessToken = body.accessToken;
  return body.user;
}

/**
 * Ob diese Anlage eine Vorfuehranlage ist.
 *
 * Wird von der Anmeldemaske gefragt, also bevor jemand angemeldet ist. Auf
 * jeder gewoehnlichen Anlage gibt es den Pfad nicht und die Antwort ist 404;
 * das ist keine Stoerung, sondern genau die Auskunft "keine Demo". Deshalb
 * wird hier still auf false zurueckgefallen, statt einen Fehler zu zeigen.
 */
export async function demostand(): Promise<{ demo: boolean; codeNoetig: boolean }> {
  try {
    const res = await fetch("/api/v1/demo");
    if (!res.ok) return { demo: false, codeNoetig: false };
    return (await res.json()) as { demo: boolean; codeNoetig: boolean };
  } catch {
    return { demo: false, codeNoetig: false };
  }
}

/**
 * Die Demo oeffnen.
 *
 * Ohne Mailadresse und ohne Passwort: welches Konto gemeint ist, weiss der
 * Server. Ein Demopasswort im Frontend stuende im ausgelieferten Bundle und
 * damit offen im Netz.
 */
export async function demoOeffnen(rolle: "buero" | "mitarbeiter", code: string): Promise<SessionUser> {
  const body = await request<{ accessToken: string; user: SessionUser }>("/auth/demo", {
    method: "POST",
    body: JSON.stringify({ rolle, code })
  });
  accessToken = body.accessToken;
  return body.user;
}

export async function refresh(): Promise<SessionUser | null> {
  try {
    const res = await fetch("/api/v1/auth/refresh", { method: "POST", credentials: "include" });
    if (!res.ok) return null;
    const body = (await res.json()) as { accessToken: string; user: SessionUser };
    accessToken = body.accessToken;
    return body.user;
  } catch {
    return null;
  }
}

/** Passwort aendern. Danach sind alle Sitzungen beendet, auch die eigene. */
export async function changePassword(currentPassword: string, newPassword: string) {
  await request("/auth/password", {
    method: "POST",
    body: JSON.stringify({ currentPassword, newPassword })
  });
  accessToken = null;
}

export async function logout() {
  await fetch("/api/v1/auth/logout", { method: "POST", credentials: "include" });
  accessToken = null;
}

/**
 * Eine Einladung annehmen und damit angemeldet sein.
 *
 * Der Weg fuer neue Mitarbeiter: sie bekommen einen Link, setzen ihr Passwort
 * selbst und sind danach drin. Niemand vergibt ein Passwort und sagt es
 * weiter.
 */
export async function einladungAnnehmen(token: string, password: string): Promise<SessionUser> {
  const body = await request<{ accessToken: string; user: SessionUser }>("/auth/invitations/accept", {
    method: "POST",
    body: JSON.stringify({ token, password, deviceLabel: navigator.userAgent.slice(0, 100) })
  });
  accessToken = body.accessToken;
  return body.user;
}

export const api = {
  get: <T>(p: string) => request<T>(p),
  post: <T>(p: string, body?: unknown) =>
    request<T>(p, { method: "POST", body: body === undefined ? undefined : JSON.stringify(body) }),
  patch: <T>(p: string, body: unknown) =>
    request<T>(p, { method: "PATCH", body: JSON.stringify(body) }),
  // PUT dort, wo es einen Datensatz je Schluessel gibt und ein zweites
  // Speichern denselben ersetzt, statt einen zweiten anzulegen.
  put: <T>(p: string, body: unknown) =>
    request<T>(p, { method: "PUT", body: JSON.stringify(body) }),
  del: <T>(p: string) => request<T>(p, { method: "DELETE" }),
  request
};

/**
 * Laedt eine Datei von der API und legt sie beim Nutzer ab.
 *
 * Ein einfaches <a href> reicht hier nicht: das Zugriffstoken liegt bewusst nur
 * im Arbeitsspeicher und nicht im Cookie, der Browser wuerde es also nicht
 * mitschicken. Deshalb wird die Datei geholt, im Speicher gehalten und ueber
 * einen kurzlebigen Objekt-Verweis uebergeben. Der Verweis wird sofort wieder
 * freigegeben, sonst bleibt die Datei bis zum Neuladen im Speicher liegen.
 */
export async function dateiLaden(path: string, ersatzname: string, retry = true): Promise<void> {
  const res = await fetch(`/api/v1${path}`, {
    credentials: "include",
    headers: accessToken ? { authorization: `Bearer ${accessToken}` } : {}
  });

  if (res.status === 401 && retry && accessToken) {
    const renewed = await refresh();
    if (renewed) return dateiLaden(path, ersatzname, false);
  }

  if (!res.ok) {
    const body = await res.json().catch(() => ({}) as Record<string, string>);
    throw new ApiError(res.status, body.error ?? "error", body.message ?? "Die Datei konnte nicht geladen werden");
  }

  const name = dateinameAusKopf(res.headers.get("content-disposition")) ?? ersatzname;
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  // Erst nach dem naechsten Durchlauf freigeben, sonst bricht der Download ab.
  setTimeout(() => URL.revokeObjectURL(url), 5000);
}

function dateinameAusKopf(kopf: string | null): string | null {
  if (!kopf) return null;
  const treffer = /filename="([^"]+)"/.exec(kopf);
  return treffer ? treffer[1] : null;
}

/**
 * Eine Datei hochladen.
 *
 * Bewusst nicht ueber request(): dort setzt der Browser den Inhaltstyp auf
 * JSON. Bei multipart muss er ihn selbst setzen, denn nur er kennt die
 * Trennmarke zwischen den Teilen. Wer hier von Hand "multipart/form-data"
 * eintraegt, bekommt einen Upload, den kein Server lesen kann.
 */
export async function dateiSenden<T>(
  path: string,
  form: FormData,
  retry = true
): Promise<T> {
  const res = await fetch(`/api/v1${path}`, {
    method: "POST",
    credentials: "include",
    headers: accessToken ? { authorization: `Bearer ${accessToken}` } : {},
    body: form
  });

  if (res.status === 401 && retry && accessToken) {
    const renewed = await refresh();
    if (renewed) return dateiSenden<T>(path, form, false);
  }

  const body = await res.json().catch(() => ({}) as Record<string, string>);
  if (!res.ok) {
    throw new ApiError(res.status, body.error ?? "error", body.message ?? "Der Upload hat nicht geklappt");
  }
  return body as T;
}

/**
 * Ein Bild holen und als Objektverweis zurueckgeben.
 *
 * Ein schlichtes <img src="/api/v1/media/..."> funktioniert hier NICHT: der
 * Browser haengt an eine Bildanfrage keinen Authorization-Kopf, und unser
 * Zugriffstoken liegt bewusst nur im Arbeitsspeicher. Die Antwort waere jedes
 * Mal 401 und der Nutzer saehe ein kaputtes Bild. Deshalb wird das Bild wie
 * jede andere Anfrage geholt und danach als Verweis eingehaengt.
 *
 * Der Aufrufer muss den Verweis wieder freigeben, sonst bleibt das Bild bis
 * zum Neuladen der Seite im Speicher.
 */
/*
 * Uebergeben wird der vollstaendige Pfad, nicht nur die Kennung.
 *
 * Vorher hing die Adresse fest an /media/. Bilder kommen aber aus mehreren
 * Routen, und der Aufruf ergab dann /api/v1/media//andere-route/... Das ist
 * keine Adresse, die es gibt: der Server antwortete mit 404 und im Backoffice
 * blieb ein leeres graues Feld stehen, ohne jeden Hinweis.
 */
export async function bildLaden(pfad: string, retry = true): Promise<string> {
  const res = await fetch(`/api/v1${pfad}`, {
    credentials: "include",
    headers: accessToken ? { authorization: `Bearer ${accessToken}` } : {}
  });
  if (res.status === 401 && retry && accessToken) {
    const renewed = await refresh();
    if (renewed) return bildLaden(pfad, false);
  }
  if (!res.ok) throw new ApiError(res.status, "error", "Das Bild liess sich nicht laden");
  return URL.createObjectURL(await res.blob());
}

/**
 * Macht aus einem beliebigen Fehler den Satz, der in der Oberflaeche steht.
 * Fehler aus der API tragen bereits einen verstaendlichen deutschen Text,
 * alles andere ist ein Netzproblem und bekommt einen eigenen Hinweis.
 */
export function fehlertext(err: unknown, ersatz = "Das hat nicht geklappt"): string {
  if (err instanceof ApiError) return err.message;
  return ersatz;
}
